import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { useAppStateSelector } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import type { AppState } from '@src/providers/AppStateProvider/types';

export interface CoinMetricsCredentialsState {
  apiKey: string | null;
  /**
   * Whether the most recent pro-tier CoinMetrics request failed authentication (invalid
   * `ui.CoinMetricsApiKey`). Set from the CoinMetrics query layer: `true` on an auth error,
   * `false` on any pro response that authenticated (success or a non-auth error). Reset when
   * the key changes so a corrected key isn't reported as invalid until the next request runs.
   */
  apiKeyInvalid: boolean;
}

const initialState: CoinMetricsCredentialsState = {
  apiKey: null,
  apiKeyInvalid: false,
};

const coinMetricsCredentialsSlice = createSlice({
  name: 'coinMetricsCredentials',
  initialState,
  reducers: {
    setCoinMetricsApiKey: (state, action: PayloadAction<string | null>) => {
      if (state.apiKey !== action.payload) {
        // A new key invalidates the previous validity verdict — clear it so the next request decides.
        state.apiKeyInvalid = false;
      }
      state.apiKey = action.payload;
    },
    setCoinMetricsApiKeyInvalid: (state, action: PayloadAction<boolean>) => {
      state.apiKeyInvalid = action.payload;
    },
  },
});

export const { setCoinMetricsApiKey, setCoinMetricsApiKeyInvalid } = coinMetricsCredentialsSlice.actions;
export const coinMetricsCredentialsReducer = coinMetricsCredentialsSlice.reducer;

export const selectCoinMetricsCredentials = (state: AppState): CoinMetricsCredentialsState =>
  state.coinMetricsCredentials;
export const selectCoinMetricsApiKey = (state: AppState): string | null => state.coinMetricsCredentials.apiKey;
export const selectCoinMetricsApiKeyInvalid = (state: AppState): boolean => state.coinMetricsCredentials.apiKeyInvalid;

/**
 * Returns the CoinMetrics Pro API key from Redux.
 *
 * Use the returned value in the `skip` guard for pro-tier endpoint hooks:
 * @example
 * const apiKey = useCoinMetricsApiKey();
 * const { data } = useSomeProEndpointQuery(params, { skip: !apiKey });
 *
 * Community-tier endpoint hooks need no skip guard — their base URL is a static constant.
 */
export const useCoinMetricsApiKey = () => useAppStateSelector(selectCoinMetricsApiKey);

/**
 * Returns whether the org's CoinMetrics Pro API key was rejected by the API (invalid credentials).
 * Intel modules use this to show a dedicated "invalid API key" message instead of a generic failure.
 */
export const useCoinMetricsApiKeyInvalid = () => useAppStateSelector(selectCoinMetricsApiKeyInvalid);
